/**
 * VideoEditor — inline title + description editor for a tape.
 *
 * Trimmed to essentials: title + description + save/cancel. Year,
 * genre, RT %, IMDb lookup, and OMDb autofill were all removed once
 * the operator decided per-tape metadata wasn't useful (channel-level
 * curation covers the same UX).
 */
import React, { useState } from "react";
import axios from "axios";

export default function VideoEditor({ video, apiBase, authHeaders, onSaved, onCancel, append }) {
  const [title, setTitle] = useState(video.title || "");
  const [description, setDescription] = useState(video.description || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    append(`> SAVING "${video.title}" ...`);
    try {
      const payload = {
        title: title.trim() || null,
        description: description.trim() || null,
      };
      await axios.patch(`${apiBase}/videos/${video.id}`, payload, {
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });
      append("> METADATA SAVED.");
      await onSaved();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2 flex flex-col gap-1.5" data-testid={`video-editor-${video.id}`}>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="title"
        data-testid="edit-title"
        className="bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1 text-[11px] outline-none"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="description / episode synopsis"
        rows={2}
        data-testid="edit-description"
        className="bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1 text-[11px] outline-none resize-none"
      />
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          data-testid="edit-save"
          className="flex-1 border border-[var(--phosphor)] py-1 text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-50"
        >
          SAVE
        </button>
        <button
          onClick={onCancel}
          data-testid="edit-cancel"
          className="flex-1 border border-neutral-600 text-neutral-400 py-1 text-[11px] hover:border-neutral-400 transition-colors"
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}
